import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { ICalendarWorkTemplateBase } from "@miplatform/types";
import { addAlpha } from "@miplatform/utils";
import { Box, Tooltip, Typography } from "@mui/material";
import { getAutoAssignWindowOffsets } from "../autoAssignWindow.utils";

const buildWindowBlocks = (
  workTemplateBases: ICalendarWorkTemplateBase[],
  hourHeight: number
): AutoAssignWindowBlockData[] =>
  workTemplateBases.flatMap((template) =>
    template.autoAssignWindows.map((autoAssignWindow, windowIndex) => {
      const { top, height } = getAutoAssignWindowOffsets(
        autoAssignWindow.start,
        autoAssignWindow.end,
        hourHeight
      );

      return {
        key: `${template.id}-${windowIndex}`,
        shortName: template.shortName,
        abbreviation: template.abbreviation,
        color: addAlpha(template.color, 10),
        borderColor: template.color,
        top,
        height,
      };
    })
  );

export function AutoAssignWindows({
  workTemplateBases = [],
  adjustedHeight = 0,
}: Readonly<AutoAssignWindowsProps>) {
  const { classes } = useStyles({ adjustedHeight });

  const blocks = useMemo(
    () => buildWindowBlocks(workTemplateBases, adjustedHeight),
    [workTemplateBases, adjustedHeight]
  );

  if (blocks.length === 0) return null;

  return (
    <Box className={classes.container} data-testid="auto-assign-windows">
      {blocks.map((block) => (
        <AutoAssignWindowBlock key={block.key} block={block} />
      ))}
    </Box>
  );
}

function AutoAssignWindowBlock({
  block,
}: Readonly<{
  block: AutoAssignWindowBlockData;
}>) {
  const { classes } = useWindowBlockStyles({
    top: block.top,
    height: block.height,
    backgroundColor: block.color,
    borderColor: block.borderColor,
  });

  return (
    <Tooltip title={block.shortName} placement="right">
      <Box className={classes.windowBlock}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.HD1}
          className={classes.label}
          color={block.borderColor}
          component="span"
        >
          {block.abbreviation || block.shortName}
        </Typography>
      </Box>
    </Tooltip>
  );
}

const useStyles = makeStyles<{ adjustedHeight: number }>()(
  (theme, { adjustedHeight }) => ({
    container: {
      position: "relative",
      width: 22,
      height: adjustedHeight > 0 ? "100%" : "auto",
      flexShrink: 0,
      backgroundColor: theme.palette.background.default,
    },
  })
);

const useWindowBlockStyles = makeStyles<{
  top: number;
  height: number;
  backgroundColor: string;
  borderColor: string;
}>()((theme, { top, height, backgroundColor, borderColor }) => ({
  windowBlock: {
    position: "absolute",
    left: 2,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 2,
    overflow: "hidden",
    boxSizing: "border-box",
    top,
    height,
    backgroundColor,
    border: `1px solid ${borderColor}`,
  },
  label: {
    writingMode: "vertical-rl",
  },
}));

export interface AutoAssignWindowsProps {
  workTemplateBases?: ICalendarWorkTemplateBase[];
  adjustedHeight?: number;
}

interface AutoAssignWindowBlockData {
  key: string;
  shortName: string;
  color: string;
  top: number;
  height: number;
  borderColor: string;
  abbreviation?: string;
}
